import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, Plus, EyeOff, ChevronDown, Search, MoreHorizontal, ArrowRight, Users, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CompanyLogo } from "@/components/common";
import { EvidenceLabel, ConfidenceChip, ScorePill, ScoreBreakdown, TimingChip, SourceLine, ValueRange, IntelStageChip, kindLabel } from "@/components/intel/primitives";
import { useIntel } from "@/components/intel/context";

function Field({ label, kind, children, className }) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="mb-1 flex items-center gap-1.5">
        <span className="eyebrow text-[10px]">{label}</span>
        {kind ? <EvidenceLabel kind={kind} compact /> : null}
      </div>
      <div className="text-[13px] leading-snug">{children}</div>
    </div>
  );
}

/** The Today feed card. Answers who / what / why / evidence / what-to-sell / why-now / next-action without scrolling. */
export function OpportunityCard({ view, rank, className }) {
  const navigate = useNavigate();
  const intel = useIntel();
  const [showScore, setShowScore] = useState(false);
  const v = view;
  const c = v.company || {};
  const lead = v.lead_signal;
  const hot = v.timing === "urgent" || v.score >= 80;
  const openCompany = () => navigate(`/intel/companies/${v.company_id}`);
  const createLabel = v.opportunity ? (v.opportunity.linked_opportunity_id ? "In pipeline" : "Hand off") : "Create opportunity";

  return (
    <article
      data-testid="today-opportunity-card"
      data-project-id={v.project_id}
      className={cn("surface lift relative overflow-hidden", hot && "before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-[hsl(var(--primary)/0.6)]", className)}
    >
      <div className="px-5 pt-5">
        {/* header row */}
        <div className="flex items-start gap-3.5">
          <button type="button" onClick={openCompany} className="shrink-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40" aria-label={`Open ${c.name}`}>
            <CompanyLogo name={c.name} domain={c.website} size="md" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <button type="button" onClick={openCompany} className="truncate text-left text-[15px] font-semibold tracking-[-0.01em] hover:text-primary focus-visible:outline-none focus-visible:underline" data-testid="today-opportunity-company-name">{c.name}</button>
              <span className={cn("rounded-md px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-[0.1em]", v.kind === "expansion" ? "bg-[hsl(var(--stage-discovery)/0.12)] text-[hsl(var(--stage-discovery))]" : "bg-primary/10 text-primary")} data-testid="today-opportunity-kind">{kindLabel(v.kind)}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-muted-foreground">
              <span>{c.segment}</span>
              <span className="text-border">·</span>
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[c.city, c.country].filter(Boolean).join(", ")}</span>
              {c.employee_count ? (<><span className="text-border">·</span><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{c.employee_count.toLocaleString("nb-NO")}</span></>) : null}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <ScorePill score={v.score} breakdown={v.score_breakdown} testId="today-opportunity-score-value" />
            <TimingChip timing={v.timing} phase={v.phase} />
          </div>
        </div>

        {/* signal */}
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <EvidenceLabel kind="signal" />
            {lead ? <ConfidenceChip level={lead.confidence} /> : null}
            {lead ? <span className="text-[11.5px] font-medium text-muted-foreground">{intel.signalTypes?.[lead.type] || lead.type}</span> : null}
          </div>
          <h3 className="mt-1.5 text-[16px] font-semibold leading-snug tracking-[-0.015em]" data-testid="today-opportunity-signal-title">{lead?.title || v.project_name}</h3>
          {lead ? <SourceLine signal={lead} className="mt-1" /> : null}
          <p className="mt-2.5 text-[13.5px] leading-relaxed text-foreground/85" data-testid="today-opportunity-hypothesis">
            <EvidenceLabel kind="hypothesis" compact className="mr-1.5 -translate-y-px" />{v.hypothesis}
          </p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
            <EvidenceLabel kind="inference" compact className="mr-1.5 -translate-y-px" /><span className="font-medium text-foreground/70">Why now · </span>{v.why_now}
          </p>
        </div>

        {/* solution block */}
        <div className="mt-4 grid gap-x-6 gap-y-3 rounded-xl border border-black/[0.06] bg-[hsl(40_12%_98.5%)] p-4 sm:grid-cols-2">
          <Field label="Potential need" kind="hypothesis"><span className="text-foreground/85">{(v.needs || []).slice(0, 5).join(", ") || "Unknown"}</span></Field>
          <Field label="Mediability solution" kind="hypothesis">
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              {(v.solutions || []).slice(0, 3).map((s) => <span key={s.category} className="text-foreground/85">{s.category}<span className="text-muted-foreground"> ({(s.vendors || []).slice(0, 2).join(", ")})</span></span>)}
            </div>
          </Field>
          <Field label="Estimated range" kind="hypothesis"><ValueRange label={v.value_label} className="text-[14px]" testId="today-opportunity-value-range" /> <span className="text-[11.5px] text-muted-foreground">not a quote</span></Field>
          <Field label="Recommended contacts" kind="inference"><span className="text-foreground/85">{(v.roles || []).slice(0, 3).map((r) => r.role).join(" · ") || "Unknown"}</span></Field>
        </div>

        {/* next step */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex min-w-0 items-center gap-2 text-[13px]">
            <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-muted-foreground">Next:</span>
            <span className="truncate font-medium" data-testid="today-opportunity-next-action">{v.recommended_action}</span>
          </div>
          <div className="flex items-center gap-2">
            {v.opportunity ? <IntelStageChip stage={v.opportunity.stage} /> : null}
            <Button variant="ghost" size="sm" className="-mr-2 h-7 gap-1 px-2 text-[12px] text-muted-foreground" onClick={() => setShowScore((s) => !s)} aria-expanded={showScore} data-testid="today-opportunity-score-breakdown-toggle">Why this score? <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showScore && "rotate-180")} /></Button>
          </div>
        </div>
        {showScore ? <div className="fade-in pt-3"><ScoreBreakdown breakdown={v.score_breakdown} score={v.score} testId="today-opportunity-score-breakdown" /></div> : null}
      </div>

      {/* actions */}
      <div className="mt-4 flex items-center gap-2 border-t border-black/[0.06] bg-white px-5 py-3">
        <Button variant="outline" size="sm" onClick={openCompany} data-testid="today-opportunity-open-company-button">Open company <ArrowUpRight className="h-3.5 w-3.5" /></Button>
        <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => intel.openSignal(v)} data-testid="today-opportunity-research-project-button"><Search className="h-3.5 w-3.5" /> Research</Button>
        <Button variant="soft" size="sm" className="hidden md:inline-flex" onClick={() => intel.openCopilot(v, "brief")} data-testid="today-opportunity-generate-call-brief-button"><Phone className="h-3.5 w-3.5" /> Call brief</Button>
        <Button variant="soft" size="sm" className="hidden md:inline-flex" onClick={() => intel.openCopilot(v, "outreach")} data-testid="today-opportunity-write-outreach-button"><Mail className="h-3.5 w-3.5" /> Outreach</Button>
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="ghost" size="icon-sm" className="hidden text-muted-foreground md:inline-flex" onClick={() => intel.dismiss(v)} title="Dismiss" aria-label="Dismiss" data-testid="today-opportunity-dismiss-button"><EyeOff className="h-4 w-4" /></Button>
          <Button size="sm" onClick={() => intel.openCreate(v)} data-testid="today-opportunity-create-opportunity-button"><Plus className="h-3.5 w-3.5" /> {createLabel}</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" className="md:hidden" aria-label="More actions" data-testid="today-opportunity-more-button"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => intel.openSignal(v)}><Search className="h-4 w-4" /> Research project</DropdownMenuItem>
              <DropdownMenuItem onClick={() => intel.openCopilot(v, "brief")}><Phone className="h-4 w-4" /> Generate call brief</DropdownMenuItem>
              <DropdownMenuItem onClick={() => intel.openCopilot(v, "outreach")}><Mail className="h-4 w-4" /> Write outreach</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => intel.dismiss(v)} className="text-muted-foreground"><EyeOff className="h-4 w-4" /> Dismiss</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </article>
  );
}
